import { mobileApi } from 'services/api/mobileClient';
import { getAuthGeneration } from 'utils/auth';
import { isNativeMobile, nativeCall, nativeInvoke, nativeListen, type NativeCapabilities, type NativeListener } from './nativeBridge';

const INSTALLATION_KEY = 'wisefriend.mobile.installation.v1';
const OWNER_KEY = 'wisefriend.mobile.push-owner.v1';
export const TEST_NOTIFICATION_ID = 937101;
let registrationWrites: Promise<void> = Promise.resolve();
let nativeOperations: Promise<void> = Promise.resolve();
let consentGeneration = 0;
let pendingRegistration: { generation: number; consent: number; userId: number; promise: Promise<void> } | undefined;
export const MOBILE_REGISTRATION_CHANGED = 'wisefriend:mobile-registration-changed';

function publishRegistrationChange() {
  window.dispatchEvent(new Event(MOBILE_REGISTRATION_CHANGED));
}

function enqueueNative<T>(operation: () => Promise<T>): Promise<T> {
  const result = nativeOperations.catch(() => undefined).then(operation);
  nativeOperations = result.then(() => undefined, () => undefined);
  return result;
}

function assertCurrent(generation: number, consent: number) {
  if (generation !== getAuthGeneration() || consent !== consentGeneration) {
    throw new Error('Your sign-in changed or reminders were disabled. Please enable notifications again.');
  }
}

export function getInstallationId(): string {
  let id = localStorage.getItem(INSTALLATION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(INSTALLATION_KEY, id);
  }
  return id;
}

export const hasPushRegistration = (userId: number) => localStorage.getItem(OWNER_KEY) === String(userId);

const persistPushRegistration = (
  token: string, capabilities: NativeCapabilities, userId: number, generation: number, requireExistingOptIn: boolean, consent = consentGeneration,
): Promise<void> => {
  const write = registrationWrites.catch(() => undefined).then(async () => {
    assertCurrent(generation, consent);
    if (requireExistingOptIn && !hasPushRegistration(userId)) return;
    const installationId = requireExistingOptIn ? localStorage.getItem(INSTALLATION_KEY) : getInstallationId();
    if (!installationId) return;
    await mobileApi.register({
      installation_id: installationId, platform: capabilities.platform,
      provider: capabilities.platform === 'ios' ? 'apns' : 'fcm', token,
    });
  });
  registrationWrites = write.catch(() => undefined);
  return write;
};

/** Rotation callbacks are scoped to the session that received them, never a subsequent sign-in. */
export async function syncRotatedPushToken(
  token: string, capabilities: NativeCapabilities, userId: number, generation: number,
): Promise<void> {
  if (!token || token.length > 4096 || generation !== getAuthGeneration() || !hasPushRegistration(userId)) return;
  if (!capabilities.remotePush) return;
  await persistPushRegistration(token, capabilities, userId, generation, true);
}

/** Explicit enable may prompt; background refresh only checks already-granted permission. */
export function registerNativePush(capabilities: NativeCapabilities, userId: number, options: { prompt?: boolean } = {}): Promise<void> {
  const generation = getAuthGeneration();
  const consent = consentGeneration;
  if (pendingRegistration?.generation === generation && pendingRegistration.consent === consent && pendingRegistration.userId === userId) {
    return pendingRegistration.promise;
  }
  const promise = enqueueNative(async () => {
    assertCurrent(generation, consent);
    if (options.prompt === false && !hasPushRegistration(userId)) return;
    const status = await mobileApi.status();
    assertCurrent(generation, consent);
    const provider = capabilities.platform === 'ios' ? 'apns' : 'fcm';
    if (!status.enabled || !status.providers[provider] || !capabilities.remotePush) {
      throw new Error('Remote reminders are not configured yet. You can still try a notification on this device.');
    }
    const permission = await nativeInvoke<{ receive: string }>('PushNotifications', options.prompt === false ? 'checkPermissions' : 'requestPermissions');
    assertCurrent(generation, consent);
    if (permission.receive !== 'granted') throw new Error('Notifications are disabled. You can allow them in your device settings.');
    const listeners: NativeListener[] = [];
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let settled = false;
    const addListener = async <T,>(name: string, callback: (event: T) => void) => {
      const handle = await nativeListen<T>('PushNotifications', name, callback);
      if (settled) await handle.remove().catch(() => undefined);
      else listeners.push(handle);
    };
    try {
      const token = await new Promise<string>((resolve, reject) => {
        timeout = setTimeout(() => reject(new Error('Device registration timed out. Please try again.')), 20000);
        void (async () => {
          await addListener<{ value: string }>('registration', (event) => {
            if (typeof event.value === 'string' && event.value && event.value.length <= 4096) resolve(event.value);
            else reject(new Error('The device returned an invalid notification registration. Please try again.'));
          });
          await addListener('registrationError', () => reject(new Error('Unable to register this device for remote notifications.')));
          assertCurrent(generation, consent);
          if (!settled) await nativeInvoke('PushNotifications', 'register');
        })().catch(reject);
      });
      assertCurrent(generation, consent);
      await persistPushRegistration(token, capabilities, userId, generation, false, consent);
      assertCurrent(generation, consent);
      localStorage.setItem(OWNER_KEY, String(userId));
      publishRegistrationChange();
    } finally {
      settled = true;
      clearTimeout(timeout);
      await Promise.all(listeners.map((listener) => listener.remove().catch(() => undefined)));
    }
  });
  pendingRegistration = { generation, consent, userId, promise };
  void promise.finally(() => { if (pendingRegistration?.promise === promise) pendingRegistration = undefined; }).catch(() => undefined);
  return promise;
}

/** Server revocation is confirmed before reporting that this installation is disabled. */
export async function disableNativePush(): Promise<void> {
  const generation = getAuthGeneration();
  const installationId = localStorage.getItem(INSTALLATION_KEY);
  consentGeneration += 1;
  localStorage.removeItem(OWNER_KEY);
  publishRegistrationChange();
  const consent = consentGeneration;
  await enqueueNative(async () => {
    await registrationWrites.catch(() => undefined);
    assertCurrent(generation, consent);
    if (installationId) await mobileApi.unregister(installationId);
    assertCurrent(generation, consent);
    await nativeInvoke('PushNotifications', 'unregister');
    await nativeInvoke('PushNotifications', 'removeAllDeliveredNotifications');
    publishRegistrationChange();
  });
}

export async function testLocalNotification(): Promise<void> {
  const generation = getAuthGeneration();
  const consent = consentGeneration;
  return enqueueNative(async () => {
    assertCurrent(generation, consent);
    const permissions = await nativeInvoke<{ display: string }>('LocalNotifications', 'requestPermissions');
    assertCurrent(generation, consent);
    if (permissions.display !== 'granted') throw new Error('Notifications are disabled. You can allow them in your device settings.');
    // An immediate smoke notification needs no Android exact-alarm privilege.
    await nativeInvoke('LocalNotifications', 'schedule', {
      notifications: [{
        id: TEST_NOTIFICATION_ID,
        title: 'A moment for you',
        body: 'Your WiseFriend notification is ready. Tap to open your dashboard.',
        extra: { path: '/home' },
      }],
    });
  });
}

/** Start while the outgoing session is still authenticated; backend session binding is authoritative. */
export function clearNativeMobileSession(options: { leaveCall?: boolean } = {}): void {
  if (!isNativeMobile()) return;
  consentGeneration += 1;
  let installationId: string | null = null;
  let registered = false;
  try {
    installationId = localStorage.getItem(INSTALLATION_KEY);
    registered = Boolean(localStorage.getItem(OWNER_KEY));
    localStorage.removeItem(OWNER_KEY);
  } catch { /* Logout must still clear the native session when web storage is unavailable. */ }
  if (installationId && registered) void mobileApi.unregister(installationId).catch(() => undefined);
  if (options.leaveCall !== false) void nativeCall.leave().catch(() => undefined);
  publishRegistrationChange();
  void enqueueNative(async () => {
    await nativeInvoke('PushNotifications', 'unregister').catch(() => undefined);
    await nativeInvoke('PushNotifications', 'removeAllDeliveredNotifications').catch(() => undefined);
    await nativeInvoke('LocalNotifications', 'cancel', { notifications: [{ id: TEST_NOTIFICATION_ID }] }).catch(() => undefined);
    await nativeInvoke('LocalNotifications', 'removeAllDeliveredNotifications').catch(() => undefined);
  });
}
